/**
 * @name Table
 *
 * @description
 *  A generic table component that can be used throughout registrations by passing various props
 *
 * @example
 *  <Table
 *    columns={[
 *      { cell: 'col1', label: 'Column One' },
 *      { cell: 'col2', label: 'Column Two' },
 *      { cell: 'col3', label: 'Column Three', render: (row, _column) => <a href={`/path/to/${row.id}`}>{ row.col3 }</a> }
 *    ]}
 *    rows={[
 *      { id: 1, col1: 'Row 1 Column One', col2: 'Row 1 Column 2', col3: 'Row 1 Column 3' },
 *      { id: 2, col1: 'Row 2 Column One', col2: 'Row 2 Column 2', col3: 'Row 2 Column 3' },
 *      { id: 3, col1: 'Row 3 Column One', col2: 'Row 3 Column 2', col3: 'Row 3 Column 3' }
 *    ]}
 *    onDragEnd={items => console.warn(items)}
 *    draggableType="handle |  row"
 */

import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { columnsPropTypes, rowsPropTypes } from "./tablePropsTypes"
import DraggableTable from "./DraggableTable"
import BaseTable from "./BaseTable"
import { Paginate, SearchInput, SectionHeading, StatusMessage } from "components/ui"
import { Box, StackView, Dropdown, Checkbox } from "@planningcenter/tapestry-react"
import { css } from "@emotion/core"
import { colors } from "components/shared/admin_colors"
import sorter from "utils/sorter"
import { Menu, MenuButton, MenuList, MenuLink } from "@reach/menu-button"
import { reOrder } from "utils/arrays"

let sectionHeadingStyle = css`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: var(--pane--padding);
  --pane--padding: 1rem;
  background-color: var(--pane--background-color, white);
  border-radius: var(--pane--border-radius, 3px 3px 0 0);
  border: 1px solid var(--pane--border-color, ${colors.neutral_200});
`

const columnWithDefaults = columns =>
  columns.map(c => ({
    ...c,
    text: c.label,
    isSortable: c.isSortable ?? false,
    isSelectable: c.isSelectable ?? false,
    isSelected: c.isSelected ?? true
  }))

let dragTimeout

const Table = ({
  emptyTableMessage,
  emptyTableImageUrl,
  columns,
  rows: rowsFromProps = [],
  onDragEnd,
  onRowClick,
  onSelectChange,
  onColumnSelector,
  selectableField,
  draggableType,
  isLoading = false,
  onSortClick = null,
  defaultSort = null,
  defaultDir = "asc",
  heading = {},
  headingChildren = null,
  preTableContainer = null,
  status = null,
  className = "table",
  style = {},
  hasTableHeader = true,
  onSearchChange = null,
  showStatusMessage = true,
  isDeletable,
  onDeleteClick,
  searchPlacement = "aboveTable",
  pagination,
  onPaginateClick,
  overflowWrapperStyle = {},
  searchInputStyle = {},
  scopeByFilters,
  onScopeChange = null
}) => {
  const [selectedColumns, setSelectedColumns] = useState(columnWithDefaults(columns))
  const [items, setItems] = useState([])
  const [tableMessage, setTableMessage] = useState(status)
  const [searchValue, setSearchValue] = useState("")
  const [sortBy, setSortBy] = useState(defaultSort)
  const [sortDir, setSortDir] = useState(defaultDir)
  const [filterScope, setFilterScope] = useState(
    scopeByFilters ? Object.keys(scopeByFilters)[0] : null
  )

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    } else {
      handleScopeChange(filterScope)
    }
  }, [filterScope])

  useEffect(() => {
    setSelectedColumns(columnWithDefaults(columns))
    setTableMessage(status)
    setItems(sortBy ? sorter(sortBy, rowsFromProps, sortDir) : rowsFromProps)
  }, [columns, rowsFromProps, status])

  const handleScopeChange = scope => onScopeChange({ sortBy, sortDir, searchValue, scope })

  const handlePaginateClick = params =>
    onPaginateClick({
      sortBy,
      sortDir,
      searchValue,
      ...params,
      scope: filterScope
    })

  const handleSort = async ({ sortBy: column }) => {
    const dir = sortBy === column.cell && sortDir === "asc" ? "desc" : "asc"

    setSortBy(column.cell)
    setSortDir(dir)

    onSortClick
      ? await onSortClick({
          page: 1,
          sortBy: column.cell,
          sortDir: dir,
          searchValue,
          scope: filterScope
        })
      : setItems(sorter(column.cell, items, dir))
  }

  const handleSearchChange = async ({ searchValue: value }) => {
    setSearchValue(value)

    const filteredItems = await onSearchChange({
      items: rowsFromProps,
      searchValue: value,
      sortBy,
      sortDir,
      scope: filterScope
    })

    setItems(sortBy ? sorter(sortBy, filteredItems, sortDir) : filteredItems)
  }

  const handleSearchClear = async () => handleSearchChange({ searchValue: "" })

  const handleDragEnd = result => {
    const { source, destination } = result

    if (!destination || destination.index === source.index) {
      return
    }

    const newItems = reOrder(items, source.index, destination.index)

    setItems(newItems)
    setTableMessage({
      text: "Saving...",
      icon: "spinner"
    })

    if (dragTimeout) {
      clearTimeout(dragTimeout)
    }

    // Try and limit the request on dragging
    dragTimeout = setTimeout(
      () =>
        onDragEnd(newItems)
          .then(_r => {
            setTableMessage({
              text: "Saved!",
              icon: "general#check",
              type: "success"
            })
            setTimeout(() => setTableMessage(null), 2000)
          })
          .catch(_r =>
            setTableMessage({
              text: "Changes not saved",
              icon: "general#exclamation-triangle",
              type: "error"
            })
          ),
      1500
    )
  }

  const handleSelectableClick = column => {
    const newColumns = selectedColumns.map(sc =>
      sc.cell === column.cell ? { ...sc, isSelected: !column.isSelected } : sc
    )

    setSelectedColumns(newColumns)
    onColumnSelector && onColumnSelector(newColumns, column)
  }

  const searchInputStylesWhenSiblingElementsArePresent = css`
    border-top: 1px solid var(--pane--border-color);
    @media (min-width: 600px) {
      min-width: 320px;
      border-left: 1px solid var(--pane--border-color);
      border-top: 0;
    }
  `

  const renderSelectableDropdown = () => {
    const selectableColumns = selectedColumns.filter(c => c.isSelectable)
    return (
      <Dropdown
        icon={{ name: "general.threeVerticalBars" }}
        iconRight={undefined}
        title="Select columns"
        closeOnSelect={false}
      >
        <StackView>
          {selectableColumns.map(c => (
            <Checkbox
              key={c.cell}
              paddingVertical={1.5}
              paddingHorizontal={2}
              size="md"
              name="checkbox"
              value={c.cell}
              label={c.text}
              checked={c.isSelected}
              onChange={() => handleSelectableClick(c)}
            />
          ))}
        </StackView>
      </Dropdown>
    )
  }

  const renderStatusMessage = () =>
    showStatusMessage && tableMessage && <StatusMessage {...tableMessage} />

  const hasSelectableColumns = selectedColumns.some(c => c.isSelectable)

  const tableProps = {
    columns: hasSelectableColumns ? selectedColumns.filter(c => c.isSelected) : selectedColumns,
    rows: items,
    isLoading,
    className,
    style,
    hasTableHeader,
    emptyTableMessage,
    emptyTableImageUrl,
    onRowClick,
    onSortClick: handleSort,
    sortBy,
    sortDir,
    isSelectable: !!onSelectChange,
    selectableField,
    onSelectChange,
    isDeletable,
    onDeleteClick,
    searchValue,
    onSearchChange: onSearchChange && handleSearchChange,
    onSearchClear: onSearchChange && handleSearchClear,
    searchPlacement,
    overflowWrapperStyle,
    searchInputStyle
  }

  // Reset Pagination if filtered response reduces page counts
  const modifiedPagination = {
    ...pagination,
    ...(pagination?.currentPage > pagination?.totalPages && {
      currentPage: 1,
      firstPage: true,
      lastPage: false,
      nextPage: 2,
      prevPage: null
    })
  }

  return (
    <div>
      {onSearchChange && searchPlacement == "aboveHeader" && (
        <div className="pane" css={{ "--pane--padding": 0 }}>
          <StackView mediaQueries={{ sm: { axis: "horizontal", alignment: "center" } }}>
            {onScopeChange && scopeByFilters && (
              <Box flex={1} padding={1}>
                <Menu>
                  <MenuButton className="select--small select ws-nw w-a">
                    {scopeByFilters[filterScope]}
                  </MenuButton>
                  <MenuList>
                    {Object.keys(scopeByFilters).map(filter => (
                      <MenuLink
                        className="dropdown-menu-item w-100%"
                        key={filter}
                        onClick={() => {
                          setFilterScope(filter)
                        }}
                      >
                        {scopeByFilters[filter]}
                      </MenuLink>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
            )}
            <Box
              css={
                onScopeChange && scopeByFilters
                  ? searchInputStylesWhenSiblingElementsArePresent
                  : ""
              }
            >
              <SearchInput
                placeholder="Search by name"
                value={searchValue}
                onChange={e => handleSearchChange({ searchValue: e.target.value })}
                onClear={handleSearchClear}
                style={searchInputStyle}
              />
            </Box>
          </StackView>
        </div>
      )}

      {(heading.headerText || headingChildren) && (
        <SectionHeading css={sectionHeadingStyle} {...heading}>
          <StackView axis="horizontal" spacing={1} alignment="center">
            {renderStatusMessage()}
            {hasSelectableColumns && renderSelectableDropdown()}
            {headingChildren}
          </StackView>
        </SectionHeading>
      )}

      {!heading.headerText && !headingChildren && (
        <StackView axis="horizontal" spacing={1} distribution="right" alignment="center">
          {renderStatusMessage()}
          {hasSelectableColumns && renderSelectableDropdown()}
        </StackView>
      )}

      {preTableContainer && (
        <div className="pane m-0" css={{ borderRadius: 0, borderTop: 0, borderBottom: 0 }}>
          {preTableContainer}
        </div>
      )}

      {onDragEnd ? (
        <DraggableTable {...tableProps} onDragEnd={handleDragEnd} draggableType={draggableType} />
      ) : (
        <BaseTable {...tableProps} />
      )}

      {onPaginateClick && !isLoading && (
        <div
          className="pane pagination-wrapper"
          css={{
            "--pane--padding": "0",
            "--pane--border-radius": "0 0 3px 3px"
          }}
        >
          <Paginate pagination={modifiedPagination} onNavClick={handlePaginateClick} />
        </div>
      )}
    </div>
  )
}

Table.propTypes = {
  heading: PropTypes.shape({
    headerText: PropTypes.node,
    headerSubText: PropTypes.node,
    headerActions: PropTypes.arrayOf(
      PropTypes.shape({
        onClick: PropTypes.func,
        linkTo: PropTypes.string,
        text: PropTypes.string
      })
    ),
    className: PropTypes.string,
    style: PropTypes.shape({})
  }),
  headingChildren: PropTypes.node,
  preTableContainer: PropTypes.node,
  children: PropTypes.node,
  columns: columnsPropTypes,
  rows: rowsPropTypes,
  onDragEnd: PropTypes.func,
  onRowClick: PropTypes.func,
  onColumnSelector: PropTypes.func,
  draggableType: PropTypes.string,
  status: PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.string,
    type: PropTypes.string
  }),
  className: PropTypes.string,
  style: PropTypes.object,
  emptyTableMessage: PropTypes.string,
  emptyTableImageUrl: PropTypes.string,
  onSortClick: PropTypes.func,
  defaultSort: PropTypes.string,
  defaultDir: PropTypes.string,
  isSelectable: PropTypes.bool,
  onSelectChange: PropTypes.func,
  selectableField: PropTypes.string,
  isLoading: PropTypes.bool,
  hasTableHeader: PropTypes.bool,
  onSearchChange: PropTypes.func,
  showStatusMessage: PropTypes.bool,
  isDeletable: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  searchPlacement: PropTypes.oneOf(["aboveHeader", "aboveTable"]),
  onPaginateClick: PropTypes.func,
  pagination: PropTypes.shape({
    nextPage: PropTypes.number,
    prevPage: PropTypes.number,
    totalPages: PropTypes.number,
    currentPage: PropTypes.number
  }),
  overflowWrapperStyle: PropTypes.object,
  searchInputStyle: PropTypes.object,
  scopeByFilters: PropTypes.object,
  onScopeChange: PropTypes.func
}

export default Table
